import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  Download,
  Inbox as InboxIcon,
  LogOut,
  Megaphone,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import logoMark from "@/assets/smg-logo-mark.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const ATHLETE_LABELS = {
  full_name: "Full Name",
  email: "Email",
  phone: "Phone",
  age_range: "Age Range",
  city: "City",
  state: "State",
  sport: "Sport",
  level: "Current Level",
  ranking: "Ranking / League / Tour",
  social_links: "Social Media",
  website: "Website / Player Profile",
  sponsors: "Current Sponsors",
  needs: "Representation Needs",
  highlights: "Career Highlights",
  statement: "Personal Statement",
  media_kit: "Media Kit",
  created_at: "Submitted",
};

const BRAND_LABELS = {
  contact_name: "Contact Name",
  company: "Company / Brand",
  email: "Email",
  phone: "Phone",
  website: "Website",
  partnership_type: "Partnership Type",
  audience: "Target Audience",
  budget: "Budget Range",
  timing: "Campaign Timing",
  athlete_profile: "Athlete Profile Sought",
  brief: "Project Brief",
  created_at: "Submitted",
};

const LEVELS = ["Professional", "Emerging Pro", "Amateur", "Youth / NIL"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink/60 px-4 py-3 text-base text-white placeholder:text-slate-500 outline-none transition-all focus:border-volt/70 focus:ring-2 focus:ring-volt/20";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [googleError] = useState(
    () => new URLSearchParams(window.location.search).get("google_error") || ""
  );

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/auth";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await http.post("/auth/login", { email, password });
      const { data } = await http.get("/auth/me");
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-surface p-8 sm:p-10"
      >
        <img src={logoMark} alt="SMG" className="mx-auto h-14 w-auto" data-testid="admin-login-logo" />
        <h1 className="mt-6 text-center font-display text-3xl uppercase text-white">
          Admin <span className="text-volt">Access</span>
        </h1>
        <p className="mt-2 text-center text-sm text-slate-400">
          SMG Sports Management — internal use only.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <label className="block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email-input"
              className={inputCls}
              placeholder="admin@smgsportsmgt.com"
            />
          </label>
          <label className="block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password-input"
              className={inputCls}
              placeholder="••••••••••"
            />
          </label>
          {(error || googleError) && (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="admin-login-error">
              {error || googleError}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-btn"
            className="w-full rounded-full bg-volt py-3.5 font-display text-base uppercase tracking-wider text-ink transition-colors hover:bg-volt-light disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="mt-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="admin-google-login-btn"
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white py-3.5 font-semibold text-sm text-slate-800 transition-all hover:bg-slate-100"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
            <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
          </svg>
          Continue with Google
        </button>

        <a href="/" data-testid="admin-back-to-site-link" className="mt-6 block text-center font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-volt">
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}

function InquiryCard({ type, inquiry, labels, onToggleReviewed, onDelete }) {
  const [open, setOpen] = useState(false);
  const name = type === "athlete" ? inquiry.full_name : inquiry.contact_name;
  const title = type === "athlete" ? inquiry.full_name : `${inquiry.company} — ${inquiry.contact_name}`;
  const sub = type === "athlete" ? `${inquiry.level} · ${inquiry.city}, ${inquiry.state}` : inquiry.partnership_type;

  return (
    <div
      className={`rounded-2xl border p-5 transition-colors ${
        inquiry.reviewed ? "border-white/10 bg-ink/40" : "border-volt/30 bg-elev"
      }`}
      data-testid={`inquiry-card-${type}-${inquiry.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-display text-lg uppercase text-white">{title}</h3>
            {!inquiry.reviewed && (
              <span className="rounded-full bg-volt px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-ink" data-testid="inquiry-new-badge">
                New
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-slate-400">
            {sub} · {fmtDate(inquiry.created_at)}
          </p>
          <p className="mt-1 truncate text-xs text-slate-500">{inquiry.email} · {inquiry.phone || "no phone"}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setOpen(!open)}
            data-testid={`inquiry-expand-btn-${type}-${inquiry.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
          >
            {open ? <X className="h-3.5 w-3.5" /> : "Details"}
            {!open && <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => onToggleReviewed(inquiry)}
            data-testid={`inquiry-review-toggle-${type}-${inquiry.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors ${
              inquiry.reviewed
                ? "bg-volt/15 text-volt hover:bg-volt/25"
                : "border border-white/10 text-slate-300 hover:border-volt/50 hover:text-volt"
            }`}
          >
            <Check className="h-3.5 w-3.5" />
            {inquiry.reviewed ? "Reviewed" : "Mark Reviewed"}
          </button>
          <button
            onClick={() => onDelete(inquiry)}
            data-testid={`inquiry-delete-btn-${type}-${inquiry.id}`}
            className="inline-flex items-center rounded-full border border-red-500/30 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/10"
            aria-label="Delete inquiry"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5 grid gap-x-6 gap-y-2 border-t border-white/10 pt-4 sm:grid-cols-2">
          {Object.entries(labels).map(([key, label]) => (
            <div key={key} className={["statement", "brief", "highlights", "needs", "athlete_profile"].includes(key) ? "sm:col-span-2" : ""}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-200">{inquiry[key] || "—"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Inbox({ inquiries, refresh }) {
  const [type, setType] = useState("athlete");
  const [q, setQ] = useState("");

  const labels = type === "athlete" ? ATHLETE_LABELS : BRAND_LABELS;
  const list = (inquiries[type] || []).filter((i) => {
    if (!q) return true;
    const hay = Object.values(i).join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const unread = (inquiries[type] || []).filter((i) => !i.reviewed).length;

  const toggleReviewed = async (inquiry) => {
    try {
      await http.patch(`/admin/inquiries/${type}/${inquiry.id}`, { reviewed: !inquiry.reviewed });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteInquiry = async (inquiry) => {
    if (!window.confirm(`Delete this ${type} inquiry? This cannot be undone.`)) return;
    try {
      await http.delete(`/admin/inquiries/${type}/${inquiry.id}`);
      toast.success("Inquiry deleted.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await http.get(`/admin/inquiries/export?type=${type}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smg-${type}-inquiries.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-surface p-2">
          {[
            { id: "athlete", label: `Athletes (${(inquiries.athlete || []).length})` },
            { id: "brand", label: `Brands (${(inquiries.brand || []).length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`inbox-type-${t.id}`}
              className={`rounded-xl px-5 py-2.5 font-display text-sm uppercase tracking-wider transition-all ${
                type === t.id ? "bg-volt text-ink" : "text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-1 items-center gap-3 sm:flex-none">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search inquiries…"
              data-testid="inbox-search-input"
              className={`${inputCls} pl-11`}
            />
          </div>
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500" data-testid="inbox-count-line">
        {list.length} shown · {unread} unread
      </p>

      <div className="mt-4 space-y-3">
        {list.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-surface p-10 text-center" data-testid="inbox-empty-state">
            <InboxIcon className="mx-auto h-8 w-8 text-slate-600" />
            <p className="mt-3 text-sm text-slate-400">
              {q ? "No inquiries match your search." : "No inquiries yet — new submissions will appear here."}
            </p>
          </div>
        ) : (
          list.map((inquiry) => (
            <InquiryCard
              key={inquiry.id}
              type={type}
              inquiry={inquiry}
              labels={labels}
              onToggleReviewed={toggleReviewed}
              onDelete={deleteInquiry}
            />
          ))
        )}
      </div>
    </div>
  );
}

function NewsManager({ refreshKey, bumpRefresh }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", date: today, athlete: "", body: "", image: "", published: true });
  const [newsPhoto, setNewsPhoto] = useState(null);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/news");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      date: item.date || today,
      athlete: item.athlete || "",
      body: item.body || "",
      published: item.published !== false,
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ title: "", date: today, athlete: "", body: "", published: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      let payload = { ...form };
      if (newsPhoto) {
        const fd = new FormData();
        fd.append("photo", newsPhoto);
        const { data } = await http.post("/admin/news-image", fd);
        payload.image = data.path;
      }
      if (editingId) {
        await http.patch(`/admin/news/${editingId}`, payload);
        toast.success("News item updated.");
        e.target.reset();
        cancelEdit();
      } else {
        await http.post("/admin/news", payload);
        toast.success("News item published.");
        setForm({ title: "", date: today, athlete: "", body: "", image: "", published: true });
        setNewsPhoto(null);
        e.target.reset();
      }
      bumpRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this news item? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/news/${item.id}`);
      toast.success("News item deleted.");
      bumpRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={submit} ref={formRef} className="lg:col-span-5" data-testid={editingId ? "news-edit-form" : "news-add-form"}>
        <div className="rounded-2xl border border-white/10 bg-surface p-6">
          <h3 className="font-display text-xl uppercase text-white">
            {editingId ? (
              <>
                <Pencil className="mr-2 inline h-5 w-5 text-volt" />
                Edit News Item
              </>
            ) : (
              <>
                <Megaphone className="mr-2 inline h-5 w-5 text-volt" />
                Add News Item
              </>
            )}
          </h3>
          <div className="mt-5 space-y-4">
            <input required value={form.title} onChange={set("title")} placeholder="Headline *" data-testid="news-title-input" className={inputCls} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input type="date" value={form.date} onChange={set("date")} data-testid="news-date-input" className={inputCls} />
              <input value={form.athlete} onChange={set("athlete")} placeholder="Athlete (optional)" data-testid="news-athlete-input" className={inputCls} />
            </div>
            <textarea required value={form.body} onChange={set("body")} rows={5} placeholder="The story… *" data-testid="news-body-input" className={inputCls} />
            <label className="block">
              <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                {editingId ? "Replace image (optional, JPG/PNG/WebP, max 8MB)" : "Announcement graphic (optional, JPG/PNG/WebP, max 8MB)"}
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setNewsPhoto(e.target.files[0])}
                data-testid="news-photo-input"
                className="w-full cursor-pointer rounded-xl border border-white/10 bg-ink/60 px-4 py-2.5 text-sm text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-volt file:px-3 file:py-1.5 file:font-mono file:text-xs file:uppercase file:text-ink"
              />
            </label>
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="news-publish-checkbox"
                className="h-4 w-4 accent-[#84CC16]"
              />
              Visible on the site
            </label>
            <div className="flex gap-3">
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  data-testid="news-cancel-edit-btn"
                  className="flex-1 rounded-full border border-white/20 py-3 font-display text-sm uppercase tracking-wider text-slate-300 transition-colors hover:border-white/40 hover:text-white"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="news-submit-btn"
                className="flex-1 rounded-full bg-volt py-3 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light disabled:opacity-60"
              >
                {busy ? "Saving…" : editingId ? "Save Changes" : "Publish News"}
              </button>
            </div>
          </div>
        </div>
      </form>
      <div className="space-y-4 lg:col-span-7">
        {items.length === 0 && (
          <p className="rounded-2xl border border-white/10 bg-ink/60 p-6 font-mono text-xs uppercase tracking-wider text-slate-500" data-testid="news-admin-empty">
            No news yet — publish your first announcement.
          </p>
        )}
        {items.map((item) => (
          <div key={item.id} className="rounded-2xl border border-white/10 bg-surface p-5" data-testid={`news-item-card-${item.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  {item.date}
                  {item.athlete ? ` · ${item.athlete}` : ""}
                  {!item.published && " · hidden"}
                </p>
                <h4 className="mt-1 font-display text-lg uppercase leading-snug text-white">{item.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.body}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  onClick={() => startEdit(item)}
                  data-testid={`news-edit-btn-${item.id}`}
                  className="inline-flex items-center rounded-full border border-white/10 px-3 py-2 text-slate-300 transition-colors hover:border-volt hover:text-volt"
                  aria-label="Edit news item"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => remove(item)}
                  data-testid={`news-delete-btn-${item.id}`}
                  className="inline-flex items-center rounded-full border border-red-500/30 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/10"
                  aria-label="Delete news item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RosterManager({ refreshKey, bumpRefresh }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", level: "Professional", ranking: "", location: "", bio: "", highlights: "", media_kit: "" });
  const [file, setFile] = useState(null);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/roster");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      level: item.level || "Professional",
      ranking: item.ranking || "",
      location: item.location || "",
      bio: item.bio || "",
      highlights: item.highlights || "",
      media_kit: item.media_kit || "",
    });
    setFile(null);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", level: "Professional", ranking: "", location: "", bio: "", highlights: "", media_kit: "" });
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      if (editingId) {
        await http.patch(`/admin/roster/${editingId}`, form);
        toast.success("Athlete updated.");
        e.target.reset();
        cancelEdit();
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (file) fd.append("photo", file);
        await http.post("/admin/roster", fd);
        toast.success("Athlete added to the roster.");
        setForm({ name: "", level: "Professional", ranking: "", location: "", bio: "", highlights: "", media_kit: "" });
        setFile(null);
        e.target.reset();
      }
      bumpRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      await http.patch(`/admin/roster/${item.id}`, { published: !item.published });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove ${item.name} from the roster? This cannot be undone.`)) return;
    try {
      await http.delete(`/admin/roster/${item.id}`);
      toast.success("Athlete removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={submit} ref={formRef} className="lg:col-span-5" data-testid={editingId ? "roster-edit-form" : "roster-add-form"}>
        <div className="rounded-2xl border border-white/10 bg-surface p-6">
          <h3 className="font-display text-xl uppercase text-white">
            {editingId ? (
              <>
                <Pencil className="mr-2 inline h-5 w-5 text-volt" />
                Edit Athlete
              </>
            ) : (
              <>
                <Plus className="mr-2 inline h-5 w-5 text-volt" />
                Add Athlete
              </>
            )}
          </h3>
          <div className="mt-5 space-y-4">
            <input required value={form.name} onChange={set("name")} placeholder="Athlete name *" data-testid="roster-add-name-input" className={inputCls} />
            <select value={form.level} onChange={set("level")} data-testid="roster-add-level-select" className={`${inputCls} appearance-none`}>
              {LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
            <textarea value={form.ranking} onChange={set("ranking")} rows={2} placeholder="Ranking / League / Tour — e.g. APP Tour · Top 30" data-testid="roster-add-ranking-input" className={inputCls} />
            <input value={form.location} onChange={set("location")} placeholder="Hometown, e.g. Carlsbad, CA" data-testid="roster-add-location-input" className={inputCls} />
            <textarea value={form.bio} onChange={set("bio")} rows={3} placeholder="Short bio" data-testid="roster-add-bio-input" className={inputCls} />
            <textarea value={form.highlights} onChange={set("highlights")} rows={2} placeholder="Stat lines, separated by semicolons — e.g. 2025 Regional Champ; 28-9 season" data-testid="roster-add-highlights-input" className={inputCls} />
            <label className="block">
              <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Media Kit (PDF link or storage path — optional)</span>
              <input value={form.media_kit} onChange={set("media_kit")} placeholder="https://… or smg-sports/uploads/…" data-testid="roster-add-media-kit-input" className={inputCls} />
            </label>
            {editingId ? (
              <p className="rounded-xl border border-white/10 bg-ink/60 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="roster-edit-photo-note">
                Photo unchanged while editing
              </p>
            ) : (
              <label className="block">
                <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Photo (JPG/PNG/WebP, max 8MB)</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setFile(e.target.files[0])}
                  data-testid="roster-add-photo-input"
                  className="w-full cursor-pointer rounded-xl border border-white/10 bg-ink/60 px-4 py-2.5 text-sm text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-volt file:px-3 file:py-1.5 file:font-mono file:text-xs file:uppercase file:text-ink"
                />
              </label>
            )}
            <div className="flex gap-3">
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  data-testid="roster-cancel-edit-btn"
                  className="flex-1 rounded-full border border-white/20 py-3.5 font-display text-base uppercase tracking-wider text-slate-300 transition-colors hover:border-white/40 hover:text-white"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="roster-submit-btn"
                className="flex-1 rounded-full bg-volt py-3.5 font-display text-base uppercase tracking-wider text-ink transition-colors hover:bg-volt-light disabled:opacity-60"
              >
                {busy ? "Saving…" : editingId ? "Save Changes" : "Add to Roster"}
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="lg:col-span-7">
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-surface p-10 text-center" data-testid="roster-admin-empty-state">
              <Users className="mx-auto h-8 w-8 text-slate-600" />
              <p className="mt-3 text-sm text-slate-400">No athletes yet — add your first signing.</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-surface p-4" data-testid={`roster-item-${item.id}`}>
                {item.photo_path ? (
                  <img src={`${API}/files/${item.photo_path}`} alt={item.name} className="h-16 w-16 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-volt/15 font-display text-xl text-volt">
                    {item.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-lg uppercase text-white">{item.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
                    {item.level} {item.ranking ? `· ${item.ranking}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => startEdit(item)}
                  data-testid={`roster-edit-btn-${item.id}`}
                  className="inline-flex items-center rounded-full border border-white/10 px-3 py-2 text-slate-300 transition-colors hover:border-volt hover:text-volt"
                  aria-label="Edit athlete"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => togglePublished(item)}
                  data-testid={`roster-publish-toggle-${item.id}`}
                  className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                    item.published ? "bg-volt/15 text-volt hover:bg-volt/25" : "border border-white/10 text-slate-400 hover:text-white"
                  }`}
                >
                  {item.published ? "Live" : "Hidden"}
                </button>
                <button
                  onClick={() => remove(item)}
                  data-testid={`roster-delete-btn-${item.id}`}
                  className="inline-flex items-center rounded-full border border-red-500/30 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/10"
                  aria-label="Remove athlete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("inbox");
  const [inquiries, setInquiries] = useState({ athlete: [], brand: [] });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    document.title = "Admin | SMG Sports Management";
    let el = document.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        http.get("/admin/inquiries?type=athlete"),
        http.get("/admin/inquiries?type=brand"),
      ]);
      setInquiries({ athlete: a.data, brand: b.data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    http
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-slate-400" data-testid="admin-loading-state">
        <p className="font-mono text-xs uppercase tracking-[0.3em]">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ink text-white">
        <Login onLogin={setUser} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-logo-link">
              <img src={logoMark} alt="SMG" className="h-9 w-auto" />
            </a>
            <div className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-surface p-1.5 sm:flex">
              {[
                { id: "inbox", label: "Inbox", icon: InboxIcon },
                { id: "roster", label: "Roster", icon: Users },
                { id: "news", label: "News", icon: Megaphone },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 font-display text-sm uppercase tracking-wider transition-all ${
                    tab === t.id ? "bg-volt text-ink" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-red-500/40 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
        <div className="flex items-center gap-2 px-6 pb-3 sm:hidden">
          {[
            { id: "inbox", label: "Inbox", icon: InboxIcon },
            { id: "roster", label: "Roster", icon: Users },
            { id: "news", label: "News", icon: Megaphone },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-mobile-${t.id}`}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2 font-display text-sm uppercase tracking-wider transition-all ${
                tab === t.id ? "bg-volt text-ink" : "border border-white/10 text-slate-400"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        {tab === "inbox" ? (
          <Inbox inquiries={inquiries} refresh={refresh} />
        ) : tab === "news" ? (
          <NewsManager refreshKey={refreshKey} bumpRefresh={() => setRefreshKey((k) => k + 1)} />
        ) : (
          <RosterManager refreshKey={refreshKey} bumpRefresh={() => setRefreshKey((k) => k + 1)} />
        )}
      </main>
    </div>
  );
}
